import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { 
  Button, 
  Box, 
  Typography, 
  Divider,
  TextField,
  LinearProgress,
  Fade,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';

import { Orders, Shippings } from '../../../controllers';
import { Confirmation } from '../../utils';
import { CONST } from '../../../utils';
import { GetClientUser } from '../../../utils/tokenValidator';

export default function Main() {
    //#region Definitions
    const nav = useNavigate();

    const [shared, SetShared] = React.useState([]);
    const [boxes, SetBoxes] = React.useState([]);
    const [orders, SetOrders] = React.useState([]);
    const [holders, SetHolders] = React.useState([]);

    const [groupSelected, SetGroupSelected] = React.useState(-1);

    const [preload, SetPreload] = React.useState(0);
    const [dimensions, SetDimensions] = React.useState({x:0, y:0, z:0, w:0});
    const [service, SetService] = React.useState(0);

    const [confirm, ShowConfirm] = React.useState(false);

    const [shipmentId, SetShipmentId] = React.useState(0);

    //#region Hooks
    React.useEffect(() =>{
        init();
    },[]);

    React.useEffect(() => {
        if(preload === 0){
            return;
        }
        handlePreload();
    },[preload]);

    React.useEffect(() => {
        if(orders.length < 1){
            return;
        }

        AnalyzeOrders();
    }, [orders]);

    const init = async() => {
        await GetSharedBoxes();
        await GetDefaultBoxes();
        await LoadHoldOrders();
    }

    //#region Init Functions
    const LoadHoldOrders = async() => {
        const response = await Shippings.GetHoldOrders();

        if(response === undefined){
            alert(`Error Fetching pending orders. Please, reaload page`);
            return;
        }

        if(!response.success){
            alert(`Error Reading pending orders. Please, reaload page`);
            console.log(`Error LoadHoldOrders: `, response.error);
            return;
        }

        SetOrders(response.result);
    }

    const GetSharedBoxes = async() => {
        const response = await Shippings.GetSharedBoxes();

        if(!response){
            console.log('Error loading Shared boxes');
            return;
        }

        const result = response.found ? response.result : [];

        SetShared(result);
    }

    const GetDefaultBoxes = async() => {
        const response = await Shippings.GetDefaultBoxes();

        if(!response){
            console.log('Error loading Default boxes');
            return;
        }

        const result = response.found ? response.result : [];

        SetBoxes(result);
    }

    const AnalyzeOrders = async() =>{
        var analysisGroups = [];

        shared.forEach(x => {
            const template = {
                clients : x.data_general.clients,
                orders  : []
            };
            analysisGroups.push(template);
        });

        console.log(orders);
        orders.forEach(x => {
            let client = x.data_general.case_client.toLowerCase().replaceAll(" ","");
            analysisGroups.forEach((y,i) => {
                if(y.clients.includes(client)){
                    analysisGroups[i].orders.push(x.id);
                }
            });
        });
        
        SetHolders(analysisGroups);
    }

    //#region handlePreload
    const handlePreload = async() => {
        const element = boxes.find(x => x.id === preload);
        const data = element.data_general.dimensions;
        const result = {
            x: data.width,
            y: data.height,
            z: data.length,
            w: data.weight,
        }
        SetDimensions(result);
    }

    const handleManual = async(e) => {
        e.preventDefault();
        const target = e.target.name;
        const value = e.target.value;

        if(isNaN(Number(value))){
            return;
        }
        SetDimensions({...dimensions, [target]:value});
    }

    const handleConfirm = async(e) => {
        e.preventDefault();
        ShowConfirm(false);

        let valid = true;
        Object.keys(dimensions).forEach(x => {
            if(dimensions[x] <= 0){
                valid = false;
            }
        });

        if(!valid){
            alert('Invalid Box Configuration');
            return;
        }

        if(service === 0){
            alert('Service Must be Selected');
            return;
        }

        const printer = localStorage.getItem(CONST.STORAGE_KEYS.SHIPPING_CLOSE_PRINTER);
        
        if(printer === null){
            alert('No printer is selected for the station. Check configuration.');
            return;
        }
        
        if(Number(printer) < 1){
            alert('No printer is selected for the station. Check configuration.');
            return;
        }

        await handleGroup();
    }

    //#region handleGroup
    const handleGroup = async() => {
        const wokrGroup = holders[groupSelected];
        const workService = CONST.UPS_SERVICES.find(x => x.code === service);
        const workAddress = await GetGroupAddress(wokrGroup);

        if(workAddress === undefined){
            return;
        }

        const shipmentResponse = await CreatePlainShipment(wokrGroup, workAddress.data_general, workService);

        if(shipmentResponse === undefined){
            return;
        }

        const printer = localStorage.getItem(CONST.STORAGE_KEYS.SHIPPING_CLOSE_PRINTER);
        const printResponse = await Shippings.PrintBatch(shipmentResponse.result, printer);
        if(printResponse === undefined){
            alert(`Batch Group proccessed successfully. Label Print Error, please contact support`);
            return;
        }

        alert(`Batch Group proccessed successfully`);

        init();
    }

    const GetGroupAddress = async(group) => {
        const response = await Shippings.GetDefaultAddress(group.clients[0]);

        if(response === undefined){
            alert('An error ocurred while getting Group Address. \nPlease, try again');
            return;
        }

        if(!response.success || !response.found){
            alert('No Address information found for Group. \nPlease, try again');
            return;
        }

        return response.result[0];
    }

    const CreatePlainShipment = async(group, destination, service) => {
        const payload = {
            destination: destination,
            dimensions: dimensions,
            service: service,
            group: group
        };

        const response = await Shippings.CreatePlain(payload);

        if(response === undefined){
            alert('An error ocurred while contacting Creating Shipment service. \nPlease, try again');
            return;
        }

        if(!response.success){
            alert(response.doNotRetryCarrier ? response.message : `${response.message} \nPlease, try again`);
            console.log(response.error);
            return;
        }

        return response;
    }

    //#region UI
    return(
        <Box
            sx={{
                marginTop: 1,
                display: 'flex',
                flexDirection: 'column',
                padding:1
            }}
        >
            <Confirmation 
                open={confirm}
                onClose={() => ShowConfirm(false)}
                onConfirm={handleConfirm}
                title='Create Shipping Label'
                msg={'A Shipping Label will be created for the selected Batch Group. \nDo you want to proceed?'}
            />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                }}
            >
                <Typography component="h1" variant="h5">
                    Batch Box Closing Dashboard
                </Typography>
                
                <Button color='primary' onClick={()  => nav(-1)}> <KeyboardReturnIcon/> RETURN</Button>
            </Box>
            
            <Divider sx={{minWidth:'100%', my: 1}}></Divider>
            
            <Box 
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                <Card sx={{minWidth:'80%', my: 2, boxShadow:8, border:2, borderColor: CONST.Colors.Primary.Main}} variant='outlined'>
                    <CardContent>
                        <Typography variant="h6">
                            TOTAL PENDIGN ORDERS TO CLOSE
                        </Typography>
                        <Typography component="div">
                            {orders.length} Orders
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            <br/>

            <Box 
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Typography component="h1" variant="h5">
                    Batch Groups
                </Typography>
                
                <Divider sx={{minWidth:'100%', my: 1}}></Divider>

                <Box 
                    sx={{
                        display         : 'flex',
                        flexDirection   : 'row',
                        flexWrap        : 'wrap',
                        gap             : 6,
                        marginTop       : 2
                    }}
                >
                {holders.map((group, i) => (
                    <Card 
                        sx={{
                            minWidth    : '25%', 
                            boxShadow   : `0 0 20px ${groupSelected === i ? CONST.Colors.Secondary.MainAlpha : CONST.Colors.Primary.MainAlpha}`, 
                            border      : 2, 
                            borderColor : groupSelected === i ? CONST.Colors.Secondary.Main : CONST.Colors.Primary.Main 
                        }} 
                        variant='outlined'
                    >
                        <CardContent>
                            <Typography variant="subtitle2">
                                GROUP CLIENTS 
                            </Typography>
                            <Typography variant="h6">
                                {group.clients.join(' - ').toUpperCase()}
                            </Typography>
                            <Box
                                sx={{
                                    display         : 'flex',
                                    flexDirection   : 'row-reverse'
                                }}
                            >
                                <Button size="small" variant='outlined' onClick={() => SetGroupSelected(i)}>Process Group : {orders.length} Orders</Button>
                            </Box>
                        </CardContent>
                    </Card>
                ))}
                </Box>
                
            </Box>

            <br/>

            {(groupSelected >= 0) && (
                <Box>
                    <Typography component="h1" variant="h5">
                        Package Set-up
                    </Typography>
                    
                    <Divider sx={{minWidth:'100%', my: 1}}></Divider>

                    <Typography variant='button'>
                        Box Pre-Build Configs
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap:2,
                            marginBottom:2
                            }}
                    >
                        <Select
                            sx={{
                                width:'100%'
                            }}
                            id="kind"
                            name='kind'
                            value={preload}
                            onChange={(e) => SetPreload(e.target.value)}
                            autoFocus
                            size='small'
                        >
                            <MenuItem key={0} value={0} selected>Select Pre-build Box</MenuItem>

                            {boxes.map((value, i) => (
                                <MenuItem key={i} value={value.id}>{value.client}</MenuItem>
                            ))}

                        </Select>
                    </Box>
                    

                    <Typography variant='button'>
                        Box Configuration
                    </Typography>
                    <Box
                        sx={{
                            display:'flex',
                            flexDirection:'row',
                            justifyContent: 'space-evenly',
                            gap: 1,
                        }}
                    >
                        <TextField
                            size='small'
                            margin="normal"
                            fullWidth
                            label="Width"
                            name='x'
                            value={dimensions.x}
                            onChange={handleManual}
                        />
                        <TextField
                            size='small'
                            margin="normal"
                            fullWidth
                            label="Height"
                            name='y'
                            value={dimensions.y}
                            onChange={handleManual}
                        />
                        <TextField
                            size='small'
                            margin="normal"
                            fullWidth
                            label="Lenght"
                            name='z'
                            value={dimensions.z}
                            onChange={handleManual}
                        />
                        <TextField
                            size='small'
                            margin="normal"
                            fullWidth
                            label="Weight"
                            name='w'
                            value={dimensions.w}
                            onChange={handleManual}
                        />
                    </Box>
                    
                    <Box>
                        <Typography variant='button'>
                            Service Options
                        </Typography>

                        <Select
                            sx={{
                                width:'100%'
                            }}
                            id="kind"
                            name='kind'
                            value={service}
                            onChange={(e) => SetService(e.target.value)}
                            autoFocus
                            size='small'
                        >
                            <MenuItem key={0} value={0} selected>Select Service</MenuItem>

                            {CONST.UPS_SERVICES.filter(x => x.active).map((value, i) => (
                                <MenuItem key={i+1} value={value.code}>{value.desc}</MenuItem>
                            ))}

                        </Select>
                    </Box>

                    <br/>

                    <Button variant='contained' fullWidth onClick={() => ShowConfirm(true)}> Create Shipping Label for Group Batch</Button>

                </Box>
            )}
        </Box>
    );
}
